// activityClaims.js — walk/run claims that the data cannot back (2026-10-22).
// HealthKit steps and distance are ALL-DAY movement, not a walk or a run; a
// check-in, alert or brief may name a walk, run, jog or hike only when a
// workout row of that type exists for the day it names (or, with no day
// named, within the week of data). Pure, shared by the server guards
// (checkin-audit, push-guard) and the client brief. Deliberately narrow: plain
// "run"/"ran" ("ran over budget", "a run of 9 days", "running total", "in the
// long run") are never claims; only activity phrasing is.

const WD = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY = 86400000;
const parse = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d || '')); return m ? Date.UTC(+m[1], +m[2] - 1, +m[3]) : null; };
const fmt = (ms) => new Date(ms).toISOString().slice(0, 10);

const ADJ = String.raw`(?:(?:long|short|quick|brisk|morning|evening|lunchtime|afternoon|daily|easy|slow|big|good|nice|solid|late|early)\s+)?`;
const DAYREF = String.raw`(?:today|tonight|yesterday|last\s+night|this\s+(?:morning|afternoon|evening)|on\s+(?:mon|tues?|wednes|thurs?|fri|satur|sun)day)`;
// Each pattern captures the activity word in group 1 (walk|run|jog|hike|walked|ran|...).
const PATTERNS = [
  // "went (out) for a (long) walk", "got out for a run", "head out for a jog"
  new RegExp(String.raw`\b(?:went|go|goes|going|get|gets|getting|got|head(?:ed|ing|s)?|out)\s+(?:out\s+)?for\s+an?\s+${ADJ}(walk|run|jog|hike)\b`, 'i'),
  // "walked", "jogged", "hiked" as activity verbs (not "walked me through")
  new RegExp(String.raw`\b(walked|jogged|hiked)\b(?!\s+(?:me|you|us|them|him|her|through|into|away|back|out\s+of)\b)`, 'i'),
  // "ran" with a distance, duration, time of day or day
  new RegExp(String.raw`\b(ran)\b(?=\s+(?:\d|a\s+(?:mile|5k|10k|half|marathon|few|couple|quick|long)|for\s+\d|(?:\d+\s*)?(?:miles?|km|minutes?|mins?)\b|again\b|${DAYREF}))`, 'i'),
  // "your morning walk", "a run", "that hike", "the long walk" (not "the run of", "in the long run")
  new RegExp(String.raw`(?<!\bin\s)\b(?:your|a|an|another|that|the|his|her|their|yesterday's|today's|(?:mon|tues|wednes|thurs|fri|satur|sun)day's)\s+${ADJ}(walk|run|jog|hike)s?\b(?!\s+(?:of|rate|through|on\s+the)\b)(?!-)`, 'i'),
  // "walking day", "running week", "hiking streak"
  new RegExp(String.raw`\b(walking|running|jogging|hiking)\s+(?:week|day|days|streak|session|sessions|routine|habit)\b`, 'i'),
  // "three walks", "2 runs" (a count of sessions, not "two runs of three days")
  new RegExp(String.raw`\b(?:\d+|one|two|three|four|five|six|seven|several|a few|a couple of)\s+(walk|run|jog|hike)s\b(?!\s+of\b)`, 'i'),
  // "walk today", "run on Saturday", "hike yesterday"
  new RegExp(String.raw`\b(walk|run|jog|hike)s?\s+${DAYREF}\b`, 'i'),
  // "Saturday walk", "morning run", "lunchtime jog"
  new RegExp(String.raw`\b(?:(?:mon|tues|wednes|thurs|fri|satur|sun)day|morning|evening|lunchtime|afternoon)\s+(walk|run|jog|hike)s?\b(?!\s+(?:of|rate|through)\b)`, 'i'),
];
const kindOf = (w) => /walk|hik/i.test(w) ? 'walk' : 'run';
const TYPE_RE = { walk: /walk|hik/i, run: /run|jog/i };
const LABEL = { walk: 'Walk', run: 'Run' };

/** The local date a sentence refers to, or null when it names none. */
export function dayNamed(sentence, today) {
  const t = parse(today); if (t == null) return null;
  const s = String(sentence || '').toLowerCase();
  if (/\b(?:yesterday|last night)\b/.test(s)) return fmt(t - DAY);
  if (/\b(?:today|tonight|this (?:morning|afternoon|evening))\b/.test(s)) return fmt(t);
  const m = /\b(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/.exec(s);
  if (m) {
    const want = WD.indexOf(m[1]); const dow = new Date(t).getUTCDay();
    return fmt(t - ((dow - want + 7) % 7) * DAY);
  }
  return null;
}

/**
 * Walk/run claims in `text` that no workout row backs.
 * @param {string} text
 * @param {{ workouts: Array<{date,type,name?}>, today: string, windowDays?: number }} ctx
 *   workouts null/undefined = unknown, nothing is checked (returns []).
 * @returns [{ kind:'walk'|'run', phrase, day: string|null }]
 */
export function activityClaimHits(text, { workouts, today, windowDays = 7 } = {}) {
  if (!Array.isArray(workouts)) return [];
  const t = parse(today);
  const from = t != null ? fmt(t - (windowDays - 1) * DAY) : '0000-00-00';
  const to = today || '9999-12-31';
  const backed = (kind, day) => workouts.some(w => w && TYPE_RE[kind].test(`${w.type || ''} ${w.name || ''}`)
    && (day ? String(w.date || '').slice(0, 10) === day : (w.date >= from && String(w.date).slice(0, 10) <= to)));
  const hits = [];
  for (const sentence of String(text || '').split(/(?<=[.!?])\s+|\n+/)) {
    const day = dayNamed(sentence, today);
    for (const re of PATTERNS) {
      const m = re.exec(sentence);
      if (!m) continue;
      const kind = kindOf(m[1]);
      if (backed(kind, day)) continue;
      hits.push({ kind, phrase: m[0].trim(), day });
      break; // one hit per sentence is enough to reject it
    }
  }
  return hits;
}

/** The regeneration instruction for a hit. */
export function activityClaimIssue(h) {
  const when = h.day ? `on ${new Date(parse(h.day)).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', timeZone: 'UTC' })}` : 'this week';
  return `"${h.phrase}" names a ${h.kind}, but the data has no ${LABEL[h.kind]} workout ${when}: steps and distance are all-day movement, so say "steps", "daily distance" or "an active day", or leave it out.`;
}
